import { getFragmentData } from "~/__generated__"
import { FullInfluencerFragment } from "~/__generated__/graphql"
import { YouTubeIcon } from "~/common/SocialMediaIcons"
import { HelpPopover } from "~/ui/HelpPopover"
import { IconHeading } from "~/ui/IconHeading"
import {
  apiNumberDisplay,
  apiPercentDisplay,
  pricingDisplay,
} from "~/util/numbers"
import { SOCIAL_MEDIA_FEED_PRICING_FRAGMENT } from "../../api"
import { AudienceDemographics } from "./AudienceDemographics"
import { InvalidUrlMessage } from "./InvalidUrlMessage"
import { Label } from "./Label"
import { LabelHeader } from "./LabelHeader"

export const YoutubeInfo = ({
  influencer,
}: {
  influencer: FullInfluencerFragment
}) => {
  if (!influencer.youtubeProfile) {
    return null
  }

  return (
    <>
      <IconHeading
        title="Youtube"
        rightSide={
          <div className="flex flex-row items-center gap-4">
            {influencer.youtubeProfile.errorInvalidUrl && (
              <InvalidUrlMessage influencer={influencer} />
            )}
            <a
              href={influencer.youtubeProfile.url || ""}
              target="_blank"
              rel="noreferrer"
            >
              {influencer.youtubeProfile.handle}
            </a>
          </div>
        }
        icon={<YouTubeIcon />}
        className="mb-4"
      />
      <LabelHeader>Analytics</LabelHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 mb-6">
        <div>
          <Label>Subscribers</Label>
          <div>{apiNumberDisplay(influencer.youtubeProfile.subscribers)}</div>
        </div>
        <div>
          <Label>Videos: AVG Likes</Label>
          <div>
            {apiNumberDisplay(influencer.youtubeProfile.averageVideoLikes)}
          </div>
        </div>
        <div>
          <Label>Videos: AVG Views</Label>
          <div>
            {apiNumberDisplay(influencer.youtubeProfile.averageVideoViews)}
          </div>
        </div>
        <div>
          <div className="flex flex-row gap-2">
            <Label>Videos: ENG Rate</Label>
            <HelpPopover helpText="AVG Likes / AVG Views" />
          </div>
          <div>
            {apiPercentDisplay(influencer.youtubeProfile.videoEngagementRate)}
          </div>
        </div>
        <div>
          <Label>Shorts: AVG Likes</Label>
          <div>
            {apiNumberDisplay(influencer.youtubeProfile.averageShortsLikes)}
          </div>
        </div>
        <div>
          <Label>Shorts: AVG Views</Label>
          <div>
            {apiNumberDisplay(influencer.youtubeProfile.averageShortsViews)}
          </div>
        </div>
        <div>
          <div className="flex flex-row gap-2">
            <Label>Shorts: ENG Rate</Label>
            <HelpPopover helpText="AVG Likes / AVG Views" />
          </div>
          <div>
            {apiPercentDisplay(influencer.youtubeProfile.shortsEngagementRate)}
          </div>
        </div>
      </div>
      <AudienceDemographics
        analytics={influencer.youtubeProfile.profileAnalytics}
      />
      <LabelHeader>Pricing</LabelHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
        <div>
          <Label>Integration</Label>
          <div>
            {pricingDisplay(
              getFragmentData(
                SOCIAL_MEDIA_FEED_PRICING_FRAGMENT,
                influencer.youtubeProfile.integrationPricing
              )
            )}
          </div>
        </div>
        <div>
          <Label>Dedication</Label>
          <div>
            {pricingDisplay(
              getFragmentData(
                SOCIAL_MEDIA_FEED_PRICING_FRAGMENT,
                influencer.youtubeProfile.dedicationPricing
              )
            )}
          </div>
        </div>
        <div>
          <Label>Shorts</Label>
          <div>
            {pricingDisplay(
              getFragmentData(
                SOCIAL_MEDIA_FEED_PRICING_FRAGMENT,
                influencer.youtubeProfile.shortsPricing
              )
            )}
          </div>
        </div>
      </div>
      {influencer.youtubeProfile.notes && (
        <div className="bg-gray10 p-4 mt-4">
          <Label>Notes</Label>
          {influencer.youtubeProfile.notes}
        </div>
      )}
    </>
  )
}
